import { useMemo, useState } from 'react';
import { stories, storyCategories, topicCards } from '@/mocks/stories';
import StoryCard from './StoryCard';

export default function TopicExplorer() {
  const [active, setActive] = useState('全部');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return stories.filter((story) => {
      const matchCategory = active === '全部' || story.category === active;
      const haystack = `${story.title}${story.summary}${story.tags.join('')}`.toLowerCase();
      const matchQuery = q === '' || haystack.includes(q);
      return matchCategory && matchQuery;
    });
  }, [active, query]);

  return (
    <section id="topics" className="w-full bg-background-100 py-16 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="text-xs font-medium tracking-[0.2em] text-secondary-600">TOPIC INDEX</span>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">专题分类</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            把同类事件放在一起看，更容易发现套路和规律。先选一个专题方向，或者直接搜关键词定位你要追的那条线。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-6">
          {topicCards.map((topic) => (
            <button
              key={topic.name}
              type="button"
              onClick={() => setActive(topic.name)}
              className={`flex cursor-pointer flex-col rounded-lg border bg-background-50 p-4 text-left transition-colors ${
                active === topic.name
                  ? 'border-secondary-400 bg-secondary-50'
                  : 'border-background-200 hover:border-secondary-300'
              }`}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className={`${topic.icon} text-lg`} />
              </span>
              <span className="mt-3 whitespace-nowrap font-heading text-sm font-bold text-foreground-950">
                {topic.name}
              </span>
              <span className="mt-1 text-xs text-foreground-500">{topic.count} 个事件</span>
            </button>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="专题筛选">
              {storyCategories.map((category) => (
                <button
                  key={category}
                  type="button"
                  role="tab"
                  aria-selected={active === category}
                  onClick={() => setActive(category)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                    active === category
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <label className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 md:w-64">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜标题、标签或关键词"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </label>
          </div>

          <p className="text-xs text-foreground-500">
            当前筛选：<strong className="font-medium text-foreground-800">{active}</strong>
            {query.trim() !== '' && <> · 关键词「{query.trim()}」</>} · 共 {filtered.length} 条追踪记录
          </p>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-2-line text-2xl" />
            </span>
            <p className="mt-4 font-heading text-base font-bold text-foreground-900">这个组合暂时没有追踪记录</p>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              换个专题，或者清空关键词再看看。新的事件一旦出现，我们会补进最近的更新流里。
            </p>
            <button
              type="button"
              onClick={() => {
                setActive('全部');
                setQuery('');
              }}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}